/**
 * Per-row sync state — the status of background jobs that are
 * populating a row's cells. Independent of row identity + cell data;
 * a row can have cells without any sync (pure user-entered) or a
 * sync in flight without any cells yet (fetch just kicked off).
 *
 * A `RowSyncState` is a map of *sync keys* — opaque strings chosen by
 * the backend to identify individual fetches (e.g. `weather@current`,
 * `garmin_sleep@2026-04-01`). Each key has its own `SyncStatus`; the
 * row's aggregate status is the weakest of the per-key statuses:
 *
 *   any pending  → `pending`
 *   any running  → `running`   (masks any remaining pending)
 *   all terminal + any failed → `failed`
 *   all complete                → `complete`
 *
 * It is the one per-row sync state: the store keeps it, a sheet's
 * delegate hands it to the legend (`peekSyncState`), and
 * `RowSyncState.Detail` adds the sheet and fetched range it was read
 * through, for a debug surface (`client.rows.peekSyncState(id)`).
 */

import type { Option } from "../core/option.ts";
import { SdkRequestError } from "../core/errors.ts";
import { type SyncKey } from "../ids/ids.ts";
import { StageLabels } from "./sync-stages.ts";
import type { RowId } from "../ids/row-id.ts";
import type { SheetNetworkKey } from "../ids/sheet-network-key.ts";
import { DateTime, type Utc } from "@tento-chrono";

/**
 * Where one sync key's server sync stands: pending, running, complete or
 * failed.
 */
export type SyncStatus = "pending" | "running" | "complete" | "failed";

/** One sync key's server-sync progress: its `status` and an opaque `stage` label (such as `"current"`). */
export interface SyncKeyStatus {
  readonly syncKey: SyncKey;
  status: SyncStatus;
  readonly stage: Option<string>;
}

/**
 * Base shape for row attributes. Plugins extend this for per-type
 * attribute interfaces (e.g. `column-number` adds `prompt` semantics).
 * The type lives sheets-side so plugin definitions in `sheets-ui`
 * don't need to depend on the SDK package.
 */
export interface BaseAttributes {
  prompt?: string;
}

/**
 * Wire response shape for a poll-status request. Used by SDK clients
 * (e.g. `SyncClient.pollStatus`) but typed sheets-side so the canonical
 * `SyncStatus` enum is the single source of truth.
 *
 * `pollUrl` is treated as an opaque transport identifier — the
 * sheets package doesn't interpret it, just preserves it for the
 * caller to follow.
 */
export interface SyncStatusResponse {
  status: SyncStatus;
  /** The failure the server reported; `null` when none. */
  error: Option<string>;
  /** Where to poll next; `null` when the server named none. */
  pollUrl: Option<string>;
}

/**
 * Injected capability that polls one sync key's status. Backed by the
 * SDK-core `SyncClient.pollStatus` (`GET /api/sync/{syncKey}/status`),
 * threaded into the sheet at construction so `ListDataSource` can poll
 * a batch-fetch's in-flight background syncs without the sheets package
 * importing upward from `@tento/lona`.
 *
 * `signal` aborts when the caller stops waiting for this poll (the poll
 * coordinator's per-poll timeout, `SyncKeyWatcher.POLL_TIMEOUT_MS`): an
 * implementation hands it to its request, so the request is cancelled, and
 * rejects once it aborts. The coordinator does not send the key's next poll
 * until this one settles.
 */
export type SyncPoll = (syncKey: SyncKey, signal: AbortSignal) => Promise<Option<SyncStatusResponse>>;

/**
 * Rejection a `SyncPoll` raises when the server answered 429 (the per-user
 * HTTP rate limit). The poll coordinator (`SyncKeyWatcher`) pauses EVERY
 * status poll on this, rather than retrying the key on its normal cadence:
 * the limit is per user, so one 429 means the whole client is over budget
 * and every further request (mutations included) is refused until the
 * window resets.
 *
 * `retryAfterMs` carries the server's `Retry-After` when the transport can
 * see it; `null` means "unknown" and the coordinator uses its own backoff.
 *
 * An `SdkRequestError` (boundary `"sync"`, step `"poll-status"`, outcome
 * `"refused"`, status 429, reason `"rate_limited"`), so one
 * `instanceof SdkRequestError` covers it beside every other refused request.
 */
export class SyncPollThrottledError extends SdkRequestError<SdkRequestError.Refused> {
  declare readonly boundary: "sync";
  declare readonly step: "poll-status";

  constructor(readonly retryAfterMs: Option<number>) {
    super({
      boundary: "sync",
      step: "poll-status",
      method: "GET",
      path: "/api/sync/{syncKey}/status",
      failure: { outcome: "refused", status: 429 },
      reason: "rate_limited",
      details: "sync status poll throttled",
    });
    this.name = "SyncPollThrottledError";
  }
}

/**
 * A row's server-sync state: each sync key's status (`syncKeys`) and their
 * aggregate roll-up (`status`).
 */
export interface RowSyncState {
  readonly syncKeys: Map<SyncKey, SyncKeyStatus>;
  status: SyncStatus;
}

/**
 * Types and values that go with `RowSyncState`: `fromKeys`, `updateAggregate`,
 * `allTerminal`, `clone`, `stagesOf`, `Detail`.
 */
export namespace RowSyncState {
  /** Build a fresh `pending` state from a set of sync keys. */
  export function fromKeys(syncKeys: readonly SyncKey[]): RowSyncState {
    const keys = new Map<SyncKey, SyncKeyStatus>();
    for (const syncKey of syncKeys) {
      keys.set(syncKey, {
        syncKey,
        status: "pending",
        stage: StageLabels.stageOf(syncKey, null),
      });
    }
    return { syncKeys: keys, status: "pending" };
  }

  /**
   * Recompute the aggregate `status` field from the per-key statuses
   * in place. Call after mutating any `SyncKeyStatus.status`.
   */
  export function updateAggregate(state: RowSyncState): void {
    let hasRunning = false;
    let hasPending = false;
    let hasFailed = false;
    for (const ks of state.syncKeys.values()) {
      if (ks.status === "running") hasRunning = true;
      else if (ks.status === "pending") hasPending = true;
      else if (ks.status === "failed") hasFailed = true;
    }
    if (hasRunning) state.status = "running";
    else if (hasPending) state.status = "pending";
    else if (hasFailed) state.status = "failed";
    else state.status = "complete";
  }

  /** True when every sync key has reached a terminal status. */
  export function allTerminal(state: RowSyncState): boolean {
    for (const ks of state.syncKeys.values()) {
      if (ks.status === "pending" || ks.status === "running") return false;
    }
    return true;
  }

  /** Shallow-clone for persistence / snapshotting. */
  export function clone(state: RowSyncState): RowSyncState {
    const keys = new Map<SyncKey, SyncKeyStatus>();
    for (const [k, v] of state.syncKeys) {
      keys.set(k, { syncKey: v.syncKey, status: v.status, stage: v.stage });
    }
    return { syncKeys: keys, status: state.status };
  }

  /**
   * The distinct stage labels across `state`'s sync keys, in stage order —
   * numbered `_stageN` suffixes sort by N, other labels keep key order — for
   * a surface that renders staged sync progress (`weather_stage1`,
   * `weather_stage2`).
   */
  export function stagesOf(state: RowSyncState): readonly string[] {
    return StageLabels.collectStages(state);
  }

  /**
   * A row's sync state with the sheet and range it was read through, for a
   * debug surface (`client.rows.peekSyncState(id)`).
   */
  export interface Detail extends RowSyncState {
    /** The sheet the state was read through; `null` when read sheet-less. */
    readonly sheetKey: Option<SheetNetworkKey>;
    /** The range the row's cells were fetched over (empty, at now, when none was). */
    readonly range: DateTime.Range<Utc>;
    /** The row the state belongs to. */
    readonly originalRowId: RowId;
  }

  export namespace Detail {
    /**
     * `state` read through `sheetKey` over `fetchedRange`: a copy of its
     * per-key statuses (a later update does not show through) with the
     * row's context.
     */
    export function of(
      state: RowSyncState,
      rowId: RowId,
      sheetKey: Option<SheetNetworkKey>,
      fetchedRange: Option<DateTime.Range<Utc>>,
    ): Detail {
      const nowUtc = DateTime.utc.now();
      return {
        ...clone(state),
        sheetKey,
        range: fetchedRange ?? new DateTime.Range(nowUtc, nowUtc),
        originalRowId: rowId,
      };
    }
  }
}
